import React, {useLayoutEffect, useRef, useState} from 'react';
import {CheckIcon, MagnifyingGlassIcon} from '@heroicons/react/24/outline';
import {UseQueryResult, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {Link, useNavigate} from 'react-router-dom';
import {
  WyscoutImportMonths,
  WyscoutPlayerSearchResponse,
  WyscoutPlayerSearchResult,
  wyscoutImportAPI,
} from '@/features/players/wyscoutImportApi';
import LoadingStatus from '@/components/ui/LoadingStatus';
import {getV2Error} from '@/services/api/v2';

interface ScoutingWyscoutSuggestionsProps {
  workspaceId: string;
  nameQuery: string;
  returnPath: string;
  active: boolean;
  onChangeSearch: () => void;
  manualCreateHref: string;
}

interface ImportIdentity {
  workspaceId: string;
  nameQuery: string;
  months: WyscoutImportMonths;
  returnPath: string;
  generation: number;
}

interface ImportVariables extends ImportIdentity {
  wyscoutPlayerId: number;
}

const detailHref = (playerId: string, returnPath: string) => (
  `/scouting/${playerId}?${new URLSearchParams({from: returnPath})}`
);

const initials = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 2).map(word => word.charAt(0).toUpperCase()).join('') || '?';
};

const sameIdentity = (left: ImportIdentity | undefined, right: ImportIdentity): boolean => Boolean(
  left
  && left.workspaceId === right.workspaceId
  && left.nameQuery === right.nameQuery
  && left.months === right.months
  && left.returnPath === right.returnPath
  && left.generation === right.generation,
);

const ScoutingWyscoutSuggestions: React.FC<ScoutingWyscoutSuggestionsProps> = ({
  workspaceId,
  nameQuery,
  returnPath,
  active,
  onChangeSearch,
  manualCreateHref,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [months, setMonths] = useState<WyscoutImportMonths>(12);
  const trimmedQuery = nameQuery.trim();
  const currentIdentity = useRef<ImportIdentity>({workspaceId, nameQuery: trimmedQuery, months, returnPath, generation: 0});
  const mounted = useRef(true);
  const identityChanged = currentIdentity.current.workspaceId !== workspaceId
    || currentIdentity.current.nameQuery !== trimmedQuery
    || currentIdentity.current.months !== months
    || currentIdentity.current.returnPath !== returnPath;
  currentIdentity.current = {
    workspaceId,
    nameQuery: trimmedQuery,
    months,
    returnPath,
    generation: currentIdentity.current.generation + (identityChanged ? 1 : 0),
  };
  useLayoutEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useLayoutEffect(() => {
    if (!active) return;
    const queryKey = ['scouting-wyscout-suggestions', workspaceId, trimmedQuery, months] as const;
    return () => {
      void queryClient.cancelQueries({queryKey, exact: true});
    };
  }, [active, months, queryClient, trimmedQuery, workspaceId]);

  const query: UseQueryResult<WyscoutPlayerSearchResponse> = useQuery({
    queryKey: ['scouting-wyscout-suggestions', workspaceId, trimmedQuery, months],
    queryFn: ({signal}) => wyscoutImportAPI.search(workspaceId, trimmedQuery, months, signal),
    enabled: Boolean(active && workspaceId && trimmedQuery),
    retry: false,
  });

  const create = useMutation({
    mutationFn: (variables: ImportVariables) => wyscoutImportAPI.create(
      variables.workspaceId,
      variables.wyscoutPlayerId,
      variables.months,
    ),
    onSuccess: (run, variables) => {
      if (!mounted.current || !sameIdentity(variables, currentIdentity.current)) return;
      navigate(detailHref(run.playerId, variables.returnPath));
    },
    onError: (error, variables) => {
      if (!mounted.current || !sameIdentity(variables, currentIdentity.current)) return;
      const playerId = getV2Error(error).details.player_id;
      if (typeof playerId === 'string') navigate(detailHref(playerId, variables.returnPath));
    },
  });

  const mutationIsCurrent = sameIdentity(create.variables, currentIdentity.current);
  const importPending = create.isPending && mutationIsCurrent;

  const beginImport = (player: WyscoutPlayerSearchResult) => {
    if (importPending) return;
    create.mutate({...currentIdentity.current, wyscoutPlayerId: player.wyscoutPlayerId});
  };

  const createError = create.isError && mutationIsCurrent && getV2Error(create.error).details.player_id === undefined
    ? getV2Error(create.error)
    : undefined;

  if (!active) return null;

  return (
    <section
      aria-labelledby="wyscout-suggestions-heading"
      className="min-w-0 space-y-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 sm:p-5"
    >
      <div className="min-w-0">
        <h2 id="wyscout-suggestions-heading" className="text-lg font-bold text-gray-900 dark:text-gray-100">
          WyScout suggestions
        </h2>
        <p className="mt-1 break-words text-sm text-gray-700 dark:text-gray-300">
          No local players found. WyScout results match the player name only.
        </p>
      </div>

      <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200">
        History to import
        <select
          value={months}
          disabled={importPending}
          onChange={event => {
            if (!importPending) setMonths(Number(event.target.value) as WyscoutImportMonths);
          }}
          className="mt-1 block min-h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 sm:max-w-xs"
        >
          <option value={12}>12 months</option>
          <option value={24}>24 months</option>
          <option value={36}>36 months</option>
        </select>
      </label>

      {query.isFetching && !query.data && (
        <LoadingStatus
          label={`Searching WyScout for “${trimmedQuery}”…`}
          description="Checking available player profiles."
          variant="panel"
          className="justify-start border-club-blue-200 bg-club-blue-50 text-left dark:border-club-blue-800 dark:bg-club-blue-950/40 [&>div]:min-w-0 [&>div>div]:break-words [&>div>div:first-child]:font-bold"
        />
      )}

      {query.data && (
        <p role="status" aria-live="polite" className="sr-only">
          {query.data.data.length} WyScout {query.data.data.length === 1 ? 'suggestion' : 'suggestions'} found.
        </p>
      )}

      {query.isError && (
        <div role="alert" className="rounded-lg border border-danger-300 bg-danger-50 p-3 text-sm text-danger-900 dark:border-danger-700 dark:bg-danger-950 dark:text-danger-100">
          <p>{getV2Error(query.error).message}</p>
          <button
            type="button"
            onClick={() => { void query.refetch(); }}
            className="mt-1 min-h-11 rounded font-semibold underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-500"
          >
            Try search again
          </button>
        </div>
      )}

      {createError && (
        <p role="alert" className="rounded-lg border border-danger-300 bg-danger-50 p-3 text-sm text-danger-900 dark:border-danger-700 dark:bg-danger-950 dark:text-danger-100">
          {createError.message}
        </p>
      )}

      {query.data?.warnings.map(warning => (
        <p key={warning} role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
          {warning} Results below are the coverage WyScout could return.
        </p>
      ))}

      {query.isSuccess && query.data.data.length === 0 && (
        <div className="flex min-w-0 flex-col items-center rounded-xl border border-club-blue-200 bg-club-blue-50/60 p-5 text-center dark:border-gray-700 dark:bg-gray-800/50 sm:p-6">
          <div aria-hidden="true" className="flex h-12 w-12 items-center justify-center rounded-xl bg-club-blue-100 text-club-blue-700 dark:bg-club-blue-950 dark:text-club-blue-300">
            <MagnifyingGlassIcon aria-hidden="true" className="h-6 w-6" />
          </div>
          <h3 className="mt-4 max-w-full break-words text-lg font-bold text-gray-900 dark:text-gray-100">
            No players found for “{trimmedQuery}”
          </h3>
          <p className="mt-2 max-w-xl text-sm text-gray-700 dark:text-gray-300">
            We checked available WyScout profiles but could not find this player.
          </p>
          <ul className="mt-4 w-full max-w-md space-y-2 text-left text-sm text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2">
              <CheckIcon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-club-blue-700 dark:text-club-blue-300" />
              <span>Check the spelling or try the full name.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-club-blue-700 dark:text-club-blue-300" />
              <span>Try fewer words, such as a surname.</span>
            </li>
          </ul>
          <div className="mt-5 flex w-full flex-col justify-center gap-2 sm:w-auto sm:flex-row">
            <button
              type="button"
              onClick={onChangeSearch}
              className="inline-flex min-h-11 items-center justify-center rounded-md bg-club-blue-700 px-4 text-sm font-semibold text-white hover:bg-club-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500"
            >
              Change search
            </button>
            <Link
              to={manualCreateHref}
              className="inline-flex min-h-11 items-center justify-center rounded-md border border-gray-300 bg-white px-4 text-sm font-semibold text-club-blue-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-club-blue-300 dark:hover:bg-gray-800"
            >
              Add player manually
            </Link>
          </div>
        </div>
      )}

      {query.data && query.data.data.length > 0 && (
        <div className="min-w-0 space-y-3">
          {query.data.data.map(player => {
            const importing = importPending && create.variables?.wyscoutPlayerId === player.wyscoutPlayerId;
            return (
              <article
                key={player.wyscoutPlayerId}
                aria-label={player.name}
                className="flex min-w-0 flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900 sm:flex-row sm:items-center"
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  {player.imageUrl ? (
                    <img src={player.imageUrl} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" />
                  ) : (
                    <div aria-hidden="true" className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-club-navy-950 font-bold text-white">
                      {initials(player.name)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <h3 className="break-words font-bold text-gray-900 dark:text-gray-100">{player.name}</h3>
                      <span className="rounded-full bg-club-blue-100 px-2 py-0.5 text-xs font-semibold text-club-blue-800 dark:bg-club-blue-950 dark:text-club-blue-200">
                        WyScout
                      </span>
                    </div>
                    <p className="mt-1 break-words text-sm text-gray-600 dark:text-gray-300">
                      {[player.position, player.teamName].filter(Boolean).join(' | ') || 'Profile details unavailable'}
                    </p>
                  </div>
                </div>

                <div className="flex min-w-0 flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end">
                  {player.existingPlayerId ? (
                    <>
                      <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
                        Already added
                      </span>
                      <Link
                        to={detailHref(player.existingPlayerId, returnPath)}
                        className="inline-flex min-h-11 items-center rounded-md border border-gray-300 px-3 text-sm font-semibold text-club-blue-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:border-gray-600 dark:text-club-blue-300 dark:hover:bg-gray-800"
                      >
                        View Existing Player
                      </Link>
                    </>
                  ) : (
                    <button
                      type="button"
                      disabled={importPending}
                      aria-label={importing ? `Importing ${player.name}` : `Import ${player.name}`}
                      onClick={() => beginImport(player)}
                      className="inline-flex min-h-11 items-center rounded-md bg-club-blue-700 px-4 text-sm font-semibold text-white hover:bg-club-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {importing ? `Importing ${player.name}...` : `Import ${player.name}`}
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default ScoutingWyscoutSuggestions;
